import { Button, ButtonSection, ResponsiveContainer, Text } from '@capsicum/common-web';
import {
	useWCPledgeBasketFooter,
	type WCPledgeBasketFooterProps,
	WCPledgeBasketFooterVariantEnum
} from '@capsicum/features/world-championship-pledge/shared';
import Link from 'next/link';
import styled from 'styled-components';

import { useWCPledgeBasketFooterActions } from '../hooks/useWCPledgeBasketFooterActions';

export const WCPledgeBasketFooter = ({ variant }: WCPledgeBasketFooterProps) => {
	const {
		basketLengthWithLabel,
		buttonText,
		formattedTotalPledgeAmount,
		formattedTotalPledgeAmountUSDWithDoubleTilde,
		hasPrice,
		hasReachedMaximumTeams,
		insufficientBalance,
		insufficientText,
		isButtonDisabled,
		maximumTeamsReachedText,
		topupLink
	} = useWCPledgeBasketFooter({
		variant
	});

	const { onClickHandler } = useWCPledgeBasketFooterActions({ formattedTotalPledgeAmount, variant });

	return (
		<ButtonSection $alwaysFixedPosition>
			<S.Content $variant="subpage">
				<S.TextContainer>
					<S.TextWrapper $hasBorder={variant === WCPledgeBasketFooterVariantEnum.BASKET_DETAILS}>
						<Text
							color={insufficientBalance && hasPrice ? 'destructive.enabled' : 'main.enabled'}
							testID="wcPledge-basketFooter-teamCount"
							variant="text1"
						>
							{insufficientBalance && hasPrice ? insufficientText : basketLengthWithLabel}
						</Text>
						{insufficientBalance && hasPrice && (
							<S.StyledLink data-testid="wcPledge-basketFooter-topupLink" href="/nc-wallet/topup">
								{topupLink}
							</S.StyledLink>
						)}
					</S.TextWrapper>
					{hasReachedMaximumTeams && variant === WCPledgeBasketFooterVariantEnum.ITEM_SELECTION && (
						<S.TextWrapper $hasBorder={false}>
							<S.MaximumTeamsReachedText
								color="main.subtle"
								testID="wcPledge-basketFooter-maximumTeamsReachedText}"
								variant="text5"
							>
								{maximumTeamsReachedText}
							</S.MaximumTeamsReachedText>
						</S.TextWrapper>
					)}
					{hasPrice && (
						<S.PriceWrapper>
							<Text
								color={insufficientBalance ? 'destructive.enabled' : 'main.enabled'}
								testID="wcPledge-basketFooter-totalPledgeAmount"
								variant="title6"
							>
								{formattedTotalPledgeAmount}
							</Text>
							<Text
								color={insufficientBalance ? 'destructive.enabled' : 'main.subtle'}
								testID="wcPledge-basketFooter-totalPledgeAmountUSD"
								variant="text5"
							>
								{formattedTotalPledgeAmountUSDWithDoubleTilde}
							</Text>
						</S.PriceWrapper>
					)}
				</S.TextContainer>
				<Button
					color="brand"
					disabled={isButtonDisabled}
					testID="wcPledge-basketFooter-button"
					text={buttonText}
					variant="default"
					onClick={onClickHandler}
				/>
			</S.Content>
		</ButtonSection>
	);
};

namespace S {
	export const Content = styled(ResponsiveContainer)`
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0 ${({ theme }) => theme.spacings.xxxsmall};
		box-sizing: border-box;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const TextContainer = styled.div`
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		width: 100%;
	`;
	export const TextWrapper = styled.div<{ $hasBorder: boolean }>`
		display: flex;
		align-items: flex-start;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		${({ $hasBorder, theme }) => $hasBorder && `border-bottom: 1px solid ${theme.colors.borders.main.divider};`}
		width: 100%;
	`;

	export const PriceWrapper = styled.div`
		display: flex;
		align-items: center;
		column-gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const StyledLink = styled(Link)`
		color: ${({ theme }) => theme.colors.text.brand.unselected};
		${({ theme }) => theme.fonts.text.text4};
	`;

	export const MaximumTeamsReachedText = styled(Text)`
		${({ theme }) => theme.fonts.text.text5};
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
}
